import { useToast } from "@app/components/toast";
import { MATCH_KINDS } from "@app/lib/match-kinds";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { ruleConflictDialog_ExcludeTxnMutation } from "@app/queries/ruleConflictDialog_ExcludeTxnMutation.graphql";
import type { ruleConflictDialog_UpdateRuleMutation } from "@app/queries/ruleConflictDialog_UpdateRuleMutation.graphql";
import type { ruleConflictDialog_DeleteRuleMutation } from "@app/queries/ruleConflictDialog_DeleteRuleMutation.graphql";
import type { ruleConflictDialog_PreviewQuery } from "@app/queries/ruleConflictDialog_PreviewQuery.graphql";

// The rules the server flagged as able to undo the re-attribution. Shaped by the
// selection `attributeTxns` makes on its `conflictingRules` field.
export type ConflictingRule = {
  readonly id: string;
  readonly regex: string;
  readonly matchKind: string;
  readonly caseSensitive: boolean;
  readonly direction: string;
  readonly matchField: string;
  readonly isCaptureRule: boolean;
  readonly matchesCount: number;
  readonly stakeholder:
    | { readonly id: string; readonly name: string }
    | null
    | undefined;
};

const excludeTxnMutation = graphql`
  mutation ruleConflictDialog_ExcludeTxnMutation(
    $input: RuleExcludeTxnInputObject!
  ) {
    ruleExcludeTxn(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const updateRuleMutation = graphql`
  mutation ruleConflictDialog_UpdateRuleMutation(
    $input: UpdateRuleInputObject!
  ) {
    updateRule(input: $input) {
      rule {
        id
        regex
        matchKind
        matchesCount
      }
      errors {
        message
      }
    }
  }
`;

const deleteRuleMutation = graphql`
  mutation ruleConflictDialog_DeleteRuleMutation(
    $input: DeleteRuleInputObject!
  ) {
    deleteRule(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const previewQuery = graphql`
  query ruleConflictDialog_PreviewQuery(
    $ruleId: ID!
    $regex: String
    $matchKind: String
  ) {
    ruleDeltaPreview(ruleId: $ruleId, regex: $regex, matchKind: $matchKind) {
      capturedCount
      droppedCount
      truncated
    }
  }
`;

// A mutation can fail down two channels: a domain error comes back INSIDE the
// payload's `errors`, while a model validation (an edited pattern that is no
// longer a legal capture regex, say) or a transport failure arrives as the second
// callback argument. Reading only one of them reports a rejected edit as a success.
function failureIn(
  transport: ReadonlyArray<{ readonly message: string }> | null | undefined,
  payload: ReadonlyArray<{ readonly message: string }> | null | undefined
): string | null {
  return transport?.[0]?.message ?? payload?.[0]?.message ?? null;
}

const MATCH_KIND_LABEL: Record<string, string> = {
  SUBSTRING: "contains",
  PREFIX: "starts with",
  EXACT: "is exactly",
  REGEX: "matches regex",
};

/**
 * Raised right after a manual re-attribution when the server found rules that
 * would assign those transactions straight back to the counterparty they were
 * just moved away from.
 *
 * The correction itself is already safe — a MANUAL attribution outranks a rule in
 * the running app. What is NOT safe is the next import: the rule is still there,
 * still matching, and will make the same call on every transaction it sees. This
 * is the one moment the user has the context to decide what the rule should have
 * done, so it asks now rather than leaving a silent trap.
 *
 * Four ways out, narrowest first: exclude just these transactions, re-point the
 * rule at the counterparty that turned out to be right, narrow the pattern so it
 * stops over-matching (with a live count of what that costs), or delete it.
 */
export default function RuleConflictDialog({
  rules,
  txnIds,
  movedFromName,
  movedToName,
  movedToId,
  perspectiveId,
  onResolved,
  onClose,
}: {
  rules: readonly ConflictingRule[];
  txnIds: readonly string[];
  movedFromName: string;
  movedToName: string;
  // The counterparty the txns were just moved to, for the "re-point" action.
  movedToId: string;
  perspectiveId: string | null;
  // A rule was changed: the caller re-derives (the whole set of matches moved).
  onResolved: () => void;
  onClose: () => void;
}) {
  const notify = useToast();
  const [handled, setHandled] = useState<ReadonlySet<string>>(new Set());
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);

  const remaining = rules.filter((rule) => !handled.has(rule.id));

  const resolve = (ruleId: string, message: string) => {
    setBusyRuleId(null);
    setHandled((prev) => new Set(prev).add(ruleId));
    notify({ message, kind: "success" });
    onResolved();
  };
  const fail = (message: string) => {
    setBusyRuleId(null);
    notify({ message, kind: "error" });
  };

  // Exclude every re-attributed txn from the rule. The mutation is per-txn (it
  // records one EXCLUDE exception and releases that txn), so the ids are walked
  // sequentially — each one re-enriches, and firing them concurrently would race
  // on the same rule's exception set.
  const excludeAll = (rule: ConflictingRule) => {
    setBusyRuleId(rule.id);
    const step = (index: number) => {
      if (index >= txnIds.length) {
        resolve(
          rule.id,
          `Excluded ${txnIds.length} transaction${
            txnIds.length === 1 ? "" : "s"
          } from this rule.`
        );
        return;
      }
      commitMutation<ruleConflictDialog_ExcludeTxnMutation>(environment, {
        mutation: excludeTxnMutation,
        variables: {
          input: {
            ruleId: rule.id,
            txnId: txnIds[index],
            perspectiveId: perspectiveId ?? undefined,
          },
        },
        onCompleted: (res, errors) => {
          const error = failureIn(errors, res.ruleExcludeTxn?.errors);
          if (error) return fail(error);
          step(index + 1);
        },
        onError: () => fail("Could not exclude — please try again."),
      });
    };
    step(0);
  };

  const repoint = (rule: ConflictingRule) => {
    setBusyRuleId(rule.id);
    commitMutation<ruleConflictDialog_UpdateRuleMutation>(environment, {
      mutation: updateRuleMutation,
      variables: { input: { ruleId: rule.id, stakeholderId: movedToId } },
      onCompleted: (res, errors) => {
        const error = failureIn(errors, res.updateRule?.errors);
        if (error) return fail(error);
        resolve(rule.id, `This rule now points at ${movedToName}.`);
      },
      onError: () => fail("Could not re-point the rule — please try again."),
    });
  };

  const narrow = (rule: ConflictingRule, regex: string, matchKind: string) => {
    setBusyRuleId(rule.id);
    commitMutation<ruleConflictDialog_UpdateRuleMutation>(environment, {
      mutation: updateRuleMutation,
      variables: { input: { ruleId: rule.id, regex, matchKind } },
      onCompleted: (res, errors) => {
        const error = failureIn(errors, res.updateRule?.errors);
        if (error) return fail(error);
        resolve(rule.id, "Rule pattern updated.");
      },
      onError: () => fail("Could not update the pattern — please try again."),
    });
  };

  const remove = (rule: ConflictingRule) => {
    setBusyRuleId(rule.id);
    commitMutation<ruleConflictDialog_DeleteRuleMutation>(environment, {
      mutation: deleteRuleMutation,
      variables: { input: { ruleId: rule.id } },
      onCompleted: (res, errors) => {
        const error = failureIn(errors, res.deleteRule?.errors);
        if (error) return fail(error);
        resolve(rule.id, "Rule deleted.");
      },
      onError: () => fail("Could not delete the rule — please try again."),
    });
  };

  // Every rule dealt with — nothing left to ask about.
  useEffect(() => {
    if (rules.length > 0 && remaining.length === 0) onClose();
  }, [rules.length, remaining.length, onClose]);

  // A plain fixed overlay rather than a Radix Dialog: every panel that raises this
  // is ITSELF an open modal Dialog, and a nested portalled dialog lands outside
  // that modal's pointer-events guard — clickable in theory, inert in practice.
  // CreateRuleDialog is raised from the same place and does the same thing.
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rule-conflict-title"
      onClick={onClose}
    >
      <div
        className="max-h-[88vh] w-[640px] max-w-[94vw] overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2
          id="rule-conflict-title"
          className="text-lg font-bold text-gray-900"
        >
          A rule would undo this
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {remaining.length === 1 ? "A rule" : `${remaining.length} rules`}{" "}
          would attribute{" "}
          {txnIds.length === 1
            ? "this transaction"
            : `these ${txnIds.length} transactions`}{" "}
          back to{" "}
          <strong className="font-medium text-gray-900">{movedFromName}</strong>{" "}
          on the next import. Your change is safe until then — fix the rule now
          so it stops repeating the mistake.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          {remaining.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              busy={busyRuleId === rule.id}
              disabled={busyRuleId !== null && busyRuleId !== rule.id}
              txnCount={txnIds.length}
              movedToName={movedToName}
              onExclude={() => excludeAll(rule)}
              onRepoint={() => repoint(rule)}
              onNarrow={(regex, matchKind) => narrow(rule, regex, matchKind)}
              onDelete={() => remove(rule)}
            />
          ))}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Leave {remaining.length === 1 ? "it" : "them"} as{" "}
            {remaining.length === 1 ? "it is" : "they are"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleCard({
  rule,
  busy,
  disabled,
  txnCount,
  movedToName,
  onExclude,
  onRepoint,
  onNarrow,
  onDelete,
}: {
  rule: ConflictingRule;
  busy: boolean;
  disabled: boolean;
  txnCount: number;
  movedToName: string;
  onExclude: () => void;
  onRepoint: () => void;
  onNarrow: (regex: string, matchKind: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const locked = busy || disabled;

  return (
    <section className="rounded border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex items-baseline gap-2">
        <span className="text-xs uppercase tracking-wide text-amber-700">
          {rule.isCaptureRule ? "capture rule" : "rule"}
        </span>
        <span className="text-xs text-gray-500">
          {rule.matchField === "QBO_COUNTERPARTY"
            ? "QuickBooks vendor"
            : "description"}{" "}
          {MATCH_KIND_LABEL[rule.matchKind] ?? rule.matchKind.toLowerCase()}
        </span>
      </div>
      <p className="mt-1 break-all font-mono text-sm text-gray-900">
        {rule.regex}
      </p>
      <p className="mt-1 text-xs text-gray-500">
        → {rule.stakeholder?.name ?? "resolved per transaction"} · matches{" "}
        {rule.matchesCount.toLocaleString()} transaction
        {rule.matchesCount === 1 ? "" : "s"}
      </p>

      {editing ? (
        <PatternEditor
          rule={rule}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={onNarrow}
        />
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={locked}
            onClick={onExclude}
            className="rounded border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-50"
          >
            Exclude {txnCount === 1 ? "this transaction" : `these ${txnCount}`}
          </button>
          {/* Withheld for a capture rule: its `stakeholder` is only the FALLBACK
              for values no subrule claims, so re-pointing it would change a
              target this transaction never reached and quietly leave the real
              decision — the subrule — in place. Excluding or narrowing does work. */}
          {!rule.isCaptureRule && (
            <button
              type="button"
              disabled={locked}
              onClick={onRepoint}
              className="rounded border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-50"
            >
              Point it at {movedToName}
            </button>
          )}
          <button
            type="button"
            disabled={locked}
            onClick={() => setEditing(true)}
            className="rounded border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-50"
          >
            Narrow the pattern…
          </button>
          <button
            type="button"
            disabled={locked}
            onClick={() =>
              confirmingDelete ? onDelete() : setConfirmingDelete(true)
            }
            className={`rounded border px-2.5 py-1 text-sm disabled:opacity-50 ${
              confirmingDelete
                ? "border-red-600 bg-red-600 text-white hover:bg-red-700"
                : "border-gray-300 bg-white text-red-700 hover:bg-red-50"
            }`}
          >
            {confirmingDelete
              ? `Delete — releases ${rule.matchesCount.toLocaleString()}`
              : "Delete the rule"}
          </button>
          {busy && (
            <span className="self-center text-xs text-gray-500">working…</span>
          )}
        </div>
      )}
    </section>
  );
}

// The pattern editor, with a live count of what the proposed pattern would cost:
// `droppedCount` is what the rule LETS GO of (the point of narrowing), and
// `capturedCount` is what it would newly grab — which should stay at zero when
// the intent is to over-match less.
function PatternEditor({
  rule,
  busy,
  onCancel,
  onSave,
}: {
  rule: ConflictingRule;
  busy: boolean;
  onCancel: () => void;
  onSave: (regex: string, matchKind: string) => void;
}) {
  const [regex, setRegex] = useState(rule.regex);
  const [matchKind, setMatchKind] = useState(rule.matchKind);
  const [preview, setPreview] = useState<{
    capturedCount: number;
    droppedCount: number;
    truncated: boolean;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const requestRef = useRef(0);

  const unchanged = regex === rule.regex && matchKind === rule.matchKind;

  useEffect(() => {
    if (regex.trim() === "") {
      setPreview(null);
      return;
    }
    const request = ++requestRef.current;
    setPreviewing(true);
    const timer = setTimeout(() => {
      fetchQuery<ruleConflictDialog_PreviewQuery>(environment, previewQuery, {
        ruleId: rule.id,
        regex,
        matchKind,
      }).subscribe({
        next: (data) => {
          // A superseded keystroke's response must not overwrite a newer one.
          if (request !== requestRef.current) return;
          setPreview(data.ruleDeltaPreview);
          setPreviewing(false);
        },
        error: () => {
          if (request !== requestRef.current) return;
          setPreview(null);
          setPreviewing(false);
        },
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [regex, matchKind, rule.id]);

  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="flex gap-2">
        <select
          value={matchKind}
          onChange={(event) => setMatchKind(event.target.value)}
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        >
          {MATCH_KINDS.map((kind) => (
            <option key={kind.value} value={kind.value}>
              {kind.label}
            </option>
          ))}
        </select>
        <input
          value={regex}
          onChange={(event) => setRegex(event.target.value)}
          spellCheck={false}
          className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-sm"
          placeholder="The text this rule should match"
        />
      </div>
      <p className="text-xs text-gray-500">
        {MATCH_KINDS.find((kind) => kind.value === matchKind)?.hint}
      </p>
      <p className="text-xs text-gray-600" aria-live="polite">
        {previewing
          ? "Checking…"
          : preview
          ? `Would stop matching ${preview.droppedCount.toLocaleString()} · would newly match ${preview.capturedCount.toLocaleString()}${
              preview.truncated ? " (at least)" : ""
            }`
          : "Type a pattern to preview its effect."}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || unchanged || regex.trim() === ""}
          onClick={() => onSave(regex, matchKind)}
          className="rounded bg-gray-900 px-2.5 py-1 text-sm text-white hover:bg-gray-800 disabled:opacity-40"
        >
          Save pattern
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="rounded border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
